import { Link } from "react-router-dom";
import { CardComp } from "../../../../components";
import AdminModulePageShell from "../../../../components/Admin/InvestorPortal/AdminModulePageShell";
import { AdminNeedsAttentionGrid } from "../../../../components/Admin/InvestorPortal/AdminNeedsAttentionCard";
import { OrderStatusBadge, StockStatusBadge } from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import InvestorPortalTableEmpty from "../../../../components/Admin/InvestorPortal/InvestorPortalTableEmpty";
import { formatMoney } from "../../../../utils/currencyFormat";
import { convertToTitleCase } from "../../../../utils/helpers";
import { useGetStaffMarketplaceDashboardQuery } from "../../../../store/admin/marketplace/staffMarketplaceApi";
import { buildMarketplaceNeedsAttentionItems } from "../../../../store/admin/marketplace/needsAttention";
import moment from "moment";
import { withMarketplaceGuard } from "../withInvestorPortalGuard";

function MarketplaceDashboard() {
  const { data, isFetching, isError, refetch } = useGetStaffMarketplaceDashboardQuery();

  if (isFetching && !data) {
    return (
      <AdminModulePageShell title="Marketplace Overview">
        <TableSkeletonLoader rows={6} columns={3} />
      </AdminModulePageShell>
    );
  }

  if (isError && !data) {
    return (
      <AdminModulePageShell title="Marketplace Overview">
        <InvestorPortalTableEmpty
          variant="error"
          title="We couldn’t load the marketplace dashboard."
          onCta={() => refetch()}
        />
      </AdminModulePageShell>
    );
  }

  const summary = data || {};
  const needsAttention = buildMarketplaceNeedsAttentionItems({
    awaitingConfirmation: Array(summary.ordersAwaitingConfirmation || 0).fill(0),
    lowStock: Array(summary.lowStockProducts || 0).fill(0),
    outOfStock: Array(summary.outOfStockProducts || 0).fill(0),
  });

  const metricCards = [
    { label: "Total products", value: String(summary.totalProducts ?? 0) },
    { label: "Published", value: String(summary.publishedProducts ?? 0) },
    { label: "Low stock", value: String(summary.lowStockProducts ?? 0) },
    { label: "Out of stock", value: String(summary.outOfStockProducts ?? 0) },
    { label: "Submitted orders", value: String(summary.submittedOrders ?? 0) },
    { label: "Awaiting confirmation", value: String(summary.ordersAwaitingConfirmation ?? 0) },
    { label: "Processing", value: String(summary.processingOrders ?? 0) },
    {
      label: "Completed order value",
      value: formatMoney(Number(summary.completedOrderValue || 0).toFixed(2)),
    },
  ];

  return (
    <AdminModulePageShell title="Marketplace Overview" subtitle="Inventory, orders, and fulfillment at a glance.">
      <CardComp cardClassName="border-0 shadow-sm mb-4">
        <h6 className="investor-portal-section-title">Needs attention</h6>
        <p className="text-muted small mb-3">What needs your attention today?</p>
        <AdminNeedsAttentionGrid items={needsAttention} />
      </CardComp>

      <div className="row g-3 mb-4">
        {metricCards.map((c) => (
          <div key={c.label} className="col-6 col-md-4 col-xl-3">
            <CardComp cardClassName="border-0 shadow-sm h-100 metric-card">
              <p className="text-muted small mb-2">{c.label}</p>
              <h4 className="fw-bold mb-0" style={{ color: "#383838" }}>
                {c.value}
              </h4>
            </CardComp>
          </div>
        ))}
      </div>

      <div className="row g-4">
        <div className="col-lg-7">
          <CardComp cardClassName="border-0 shadow-sm">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-semibold mb-0">Recent orders</h6>
              <Link
                to="/super_admin/investor_portal/marketplace/orders"
                className="small fw-semibold"
                style={{ color: "var(--color-secondary)" }}
              >
                View all →
              </Link>
            </div>
            {(summary.recentOrders || []).length === 0 ? (
              <p className="text-muted small mb-0">No recent orders.</p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {summary.recentOrders.map((order) => (
                  <Link
                    key={order.orderNumber || order.id}
                    to={`/super_admin/investor_portal/marketplace/orders/${encodeURIComponent(order.orderNumber)}`}
                    className="text-decoration-none d-flex justify-content-between align-items-start gap-2 py-2 border-bottom"
                    style={{ color: "inherit" }}
                  >
                    <div className="min-w-0">
                      <p className="fw-semibold mb-1" style={{ color: "#383838" }}>
                        {order.orderNumber}
                      </p>
                      <p className="text-muted small mb-0">
                        {order.customerName || "Customer"}
                        {order.at ? ` · ${moment(order.at).format("DD-MMM-YYYY")}` : ""}
                      </p>
                    </div>
                    <div className="text-end flex-shrink-0">
                      <p className="fw-semibold mb-1" style={{ color: "var(--color-primary)" }}>
                        {formatMoney(order.totalAmount)}
                      </p>
                      <OrderStatusBadge status={order.status} />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardComp>
        </div>

        <div className="col-lg-5">
          <CardComp cardClassName="border-0 shadow-sm">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-semibold mb-0">Inventory alerts</h6>
              <Link
                to="/super_admin/investor_portal/marketplace/products"
                className="small fw-semibold"
                style={{ color: "var(--color-secondary)" }}
              >
                Products →
              </Link>
            </div>
            {(summary.inventoryAlerts || []).length === 0 ? (
              <p className="text-muted small mb-0">No inventory alerts.</p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {summary.inventoryAlerts.map((alert) => (
                  <Link
                    key={alert.productId}
                    to={
                      alert.slug
                        ? `/super_admin/investor_portal/marketplace/products/${alert.slug}`
                        : "/super_admin/investor_portal/marketplace/products"
                    }
                    className="text-decoration-none d-flex justify-content-between align-items-center gap-2 py-2 border-bottom"
                    style={{ color: "inherit" }}
                  >
                    <div className="min-w-0">
                      <p className="fw-semibold mb-0 text-truncate" style={{ color: "#383838" }}>
                        {alert.name}
                      </p>
                      <p className="text-muted small mb-0">
                        Qty {alert.stockQuantity ?? "—"} ·{" "}
                        {convertToTitleCase((alert.lifecycleStatus || "").replace(/_/g, " ")) || "—"}
                      </p>
                    </div>
                    <StockStatusBadge status={alert.stockStatus} />
                  </Link>
                ))}
              </div>
            )}
          </CardComp>
        </div>
      </div>
    </AdminModulePageShell>
  );
}

export default withMarketplaceGuard(MarketplaceDashboard);
